export type MonetarySeparators = {
	/** Thousands-grouping character (e.g. "," for en, "." for it). */
	groupChar: string;
	/** Decimal-separator character (e.g. "." for en, "," for it). */
	decimalChar: string;
};

/**
 * Resolves the grouping/decimal characters the given locale uses, via Intl.
 * Falls back to en-style separators when the runtime cannot resolve the locale.
 */
export function localeSeparators(locale: string | undefined): MonetarySeparators {
	try {
		const parts = new Intl.NumberFormat(locale).formatToParts(1234567.8);
		return {
			groupChar: parts.find((part) => part.type === "group")?.value ?? ",",
			decimalChar: parts.find((part) => part.type === "decimal")?.value ?? ".",
		};
	} catch {
		return { groupChar: ",", decimalChar: "." };
	}
}

export function isAsciiDigit(char: string): boolean {
	return char >= "0" && char <= "9";
}

/**
 * Reduces a displayed/typed string to a canonical numeric string: ASCII digits with at most
 * one "." decimal point. The locale decimal separator maps to "."; grouping characters and
 * any other character (letters, signs, spaces) are dropped. Only the FIRST decimal separator
 * is kept, so a stray second one cannot split the number.
 */
export function canonicalize(displayed: string, { decimalChar }: MonetarySeparators): string {
	let out = "";
	let seenDecimal = false;
	for (const char of displayed) {
		if (isAsciiDigit(char)) {
			out += char;
		} else if (char === decimalChar && !seenDecimal) {
			out += ".";
			seenDecimal = true;
		}
	}
	return out;
}

/**
 * Formats a canonical numeric string for display: thousands grouping on the integer part,
 * the decimal part — including a trailing separator still being typed — passed through
 * verbatim with the locale decimal character.
 */
export function groupFormat(canonical: string, { groupChar, decimalChar }: MonetarySeparators): string {
	if (canonical === "") {
		return "";
	}
	const decimalIndex = canonical.indexOf(".");
	const integerPart = decimalIndex === -1 ? canonical : canonical.slice(0, decimalIndex);
	const decimalPart = decimalIndex === -1 ? null : canonical.slice(decimalIndex + 1);
	const grouped = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, groupChar);
	return decimalPart === null ? grouped : `${grouped}${decimalChar}${decimalPart}`;
}

/** A three-letter currency code or a currency symbol at the start of the text, with the whitespace beside it. */
const LEADING_CURRENCY = /^\s*(?:[A-Za-z]{3}(?![A-Za-z])|\p{Sc})\s*/u;
/**
 * A three-letter currency code or a currency symbol ending the text. It carries no whitespace of its
 * own: an end-anchored `\s*` is retried from every start position, which is quadratic on a long
 * whitespace run, so {@link stripTrailingCurrency} trims around it instead.
 */
const TRAILING_CURRENCY = /(?:(?<![A-Za-z])[A-Za-z]{3}|\p{Sc})$/u;

function stripTrailingCurrency(text: string): string {
	const trimmed = text.trimEnd();
	const match = TRAILING_CURRENCY.exec(trimmed);
	return match ? trimmed.slice(0, match.index).trimEnd() : text;
}

/**
 * Reads pasted text the way it was written elsewhere: drops a leading or trailing currency code or
 * symbol ("USD 1.5m" → "1.5m"), and when both "." and "," appear, takes the last one — if it occurs
 * once and digits follow it — as the decimal separator and the other as grouping, rewritten into the
 * locale's separators ("€ 1.234,56" → "1234.56" in en). Text with a single kind of separator keeps
 * it, so the locale reading and its malformed check still apply.
 */
export function normalizePastedAmount(raw: string, { decimalChar }: MonetarySeparators): string {
	const stripped = stripTrailingCurrency(raw.replace(LEADING_CURRENCY, ""));
	if (!stripped.includes(".") || !stripped.includes(",")) {
		return stripped;
	}
	const sourceDecimal = stripped.lastIndexOf(".") > stripped.lastIndexOf(",") ? "." : ",";
	const [integerPart, fractionPart, ...extra] = stripped.split(sourceDecimal);
	if (extra.length > 0 || !/^\d/.test(fractionPart)) {
		return stripped;
	}
	const sourceGroup = sourceDecimal === "." ? "," : ".";
	return `${integerPart.split(sourceGroup).join("")}${decimalChar}${fractionPart}`;
}

const SUFFIX_EXPONENT: Record<string, number> = { k: 3, m: 6, b: 9, t: 12 };

/**
 * Expands a trailing k/m/b/t scale suffix (case-insensitive) into an INTEGER canonical
 * string ("1.5b" → "1500000000"). The scaling shifts digit strings (never floats), so the
 * expansion is exact even at the 1e13 AUM cap; fractional digits beyond the scale are
 * rounded half-up. Returns null when the input has no suffix, or when the part before the
 * suffix is not purely a locale-formatted number (e.g. "1kk").
 */
export function expandSuffix(input: string, separators: MonetarySeparators): string | null {
	const trimmed = input.trim();
	const exponent = SUFFIX_EXPONENT[trimmed.slice(-1).toLowerCase()];
	if (exponent == null) {
		return null;
	}
	const numericPart = trimmed.slice(0, -1);
	for (const char of numericPart) {
		if (!isAsciiDigit(char) && char !== separators.groupChar && char !== separators.decimalChar) {
			return null;
		}
	}
	const canonical = canonicalize(numericPart, separators);
	if (!/\d/.test(canonical)) {
		return null;
	}
	const decimalIndex = canonical.indexOf(".");
	const integerPart = decimalIndex === -1 ? canonical : canonical.slice(0, decimalIndex);
	const fractionPart = (decimalIndex === -1 ? "" : canonical.slice(decimalIndex + 1)).padEnd(exponent, "0");
	let expanded = BigInt(`${integerPart}${fractionPart.slice(0, exponent)}`);
	if (fractionPart.length > exponent && fractionPart[exponent] >= "5") {
		expanded += 1n;
	}
	return expanded.toString();
}

/**
 * Whether typed text fails to read as one amount: it has a second decimal separator, or more than
 * `fractionDigits` digits after the first.
 */
export function isMalformedAmount(
	displayed: string,
	{ decimalChar }: MonetarySeparators,
	fractionDigits: number,
): boolean {
	const [, fraction, ...extra] = displayed.split(decimalChar);
	if (extra.length > 0) {
		return true;
	}
	return fraction != null && [...fraction].filter(isAsciiDigit).length > fractionDigits;
}

/**
 * Keeps at most `fractionDigits` digits after the decimal point of a canonical numeric string,
 * dropping the rest (truncation, never rounding). A trailing decimal point still being typed is
 * kept; with `fractionDigits` 0 the decimal point and everything after it go.
 */
export function truncateFraction(canonical: string, fractionDigits: number): string {
	const decimalIndex = canonical.indexOf(".");
	if (decimalIndex === -1) {
		return canonical;
	}
	if (fractionDigits <= 0) {
		return canonical.slice(0, decimalIndex);
	}
	return canonical.slice(0, decimalIndex + 1 + fractionDigits);
}

/**
 * Trailing fraction zeros, plus the decimal point when nothing but zeros follows it:
 * "12.50" → "12.5", "12.00" → "12". Only applied to strings that contain a ".", so the zeros
 * of an integer part are never touched. Backtracking is quadratic in a run of zeros that does
 * not end the string; the input is always derived from a number (at most ~110 chars once it
 * has a "."), never from user text, so this cannot be driven into a slow match.
 */
const TRAILING_FRACTION_ZEROS = /\.?0+$/;

/**
 * Renders a number as a canonical numeric string with at most `fractionDigits` decimals,
 * truncated. Works on the number's shortest round-trip string (never on scaled floats), so a
 * value like 1234.56 never drifts to 1234.5599…; trailing zeros left by the truncation are dropped.
 */
export function numberToCanonical(value: number, fractionDigits: number): string {
	const plain = String(value);
	// Exponent notation (below 1e-6 or from 1e21) has no digit string to cut: spell the number out
	// in full, rounded to `fractionDigits` (the most Intl accepts is 100).
	const source = /e/i.test(plain)
		? value.toLocaleString("en-US", { useGrouping: false, maximumFractionDigits: Math.min(fractionDigits, 100) })
		: plain;
	const truncated = truncateFraction(source, fractionDigits);
	return truncated.includes(".") ? truncated.replace(TRAILING_FRACTION_ZEROS, "") : truncated;
}
